import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  FaBookOpen,
  FaCheckCircle,
  FaClock,
  FaGraduationCap,
  FaLock,
  FaLockOpen,
  FaRedo,
  FaShieldAlt,
  FaSignInAlt,
  FaSpinner,
} from "react-icons/fa";

import {
  courseDetailsMap,
  courseLevelLabels,
  courseLevelOrder,
  type CourseDetail,
  type CourseLevelKey,
} from "./CoursesDetails";

import Content from "./Content";
import CourseDetailSection from "./CourseDetailSection";
import Login from "../../Authentication/Login/Login";
import Payment from "../../Authentication/Payment/Payment";
import { useAuth } from "../../Authentication/AuthContext";

import "./CoursePageStyles.css";

type CourseAccessStatus =
  | "idle"
  | "checking"
  | "active"
  | "inactive"
  | "error";

type GermanCourseDisplay = {
  title: string;
  label: string;
  duration: string;
  sessions: string;
  books: string;
};

const COURSE_RESET_EVENT =
  "dear-language-course-reset";

const ACCESS_CONFIRMATION_ATTEMPTS = 4;

const ACCESS_CONFIRMATION_DELAY_MS = 1200;

const GERMAN_COURSE_DISPLAY: Record<
  CourseLevelKey,
  GermanCourseDisplay
> = {
  A1: {
    title: "A1-DEUTSCHKURS",
    label: "Anfänger",
    duration: "2 Monate",
    sessions: "2x pro Woche à 2 Stunden",
    books: "10 PDF-Bücher",
  },

  A2: {
    title: "A2-DEUTSCHKURS",
    label: "Grundstufe",
    duration: "2 Monate",
    sessions: "2x pro Woche à 2 Stunden",
    books: "10 PDF-Bücher",
  },

  B1: {
    title: "B1-DEUTSCHKURS",
    label: "Mittelstufe",
    duration: "3 Monate",
    sessions: "2x pro Woche à 2 Stunden",
    books: "10 PDF-Bücher",
  },

  B2: {
    title: "B2-DEUTSCHKURS",
    label: "Oberstufe",
    duration: "3 Monate",
    sessions: "2x pro Woche à 2 Stunden",
    books: "10 PDF-Bücher",
  },
};

const delay = (
  milliseconds: number,
): Promise<void> => {
  return new Promise((resolve) => {
    window.setTimeout(
      resolve,
      milliseconds,
    );
  });
};

const getGermanCourseDisplay = (
  level: CourseLevelKey,
): GermanCourseDisplay => {
  return (
    GERMAN_COURSE_DISPLAY[level] || {
      title: `${level}-DEUTSCHKURS`,

      label:
        courseLevelLabels[level] ||
        level,

      duration:
        "Nach Kursplan",

      sessions:
        "Unterricht nach Plan",

      books:
        "PDF-Bücher",
    }
  );
};

const CoursePage: React.FC = () => {
  const {
    user,
    userId,
    userEmail,
    isLoggedIn,
    authLoading,

    accessChecked,
    accessLoading,
    accessError,

    hasFullCourseAccess,
    accessibleCourseLevels,

    logout,
    refreshAccess,
  } = useAuth();

  const [
    selectedCourseKey,
    setSelectedCourseKey,
  ] = useState<CourseLevelKey | null>(
    null,
  );

  const [
    showLoginModal,
    setShowLoginModal,
  ] = useState<boolean>(false);

  const [
    courseAccessStatus,
    setCourseAccessStatus,
  ] = useState<CourseAccessStatus>(
    "idle",
  );

  const [
    localCourseAccessError,
    setLocalCourseAccessError,
  ] = useState<string>("");

  const [
    confirmingCourseAccess,
    setConfirmingCourseAccess,
  ] = useState<boolean>(false);

  const [
    confirmedCourseLevel,
    setConfirmedCourseLevel,
  ] = useState<CourseLevelKey | null>(
    null,
  );

  const selectedCourse:
    | CourseDetail
    | null = selectedCourseKey
    ? courseDetailsMap[
        selectedCourseKey
      ]
    : null;

  const selectedCourseLabel =
    useMemo(() => {
      if (!selectedCourseKey) {
        return "";
      }

      return getGermanCourseDisplay(
        selectedCourseKey,
      ).label;
    }, [selectedCourseKey]);

  const loggedInName =
    user?.displayName ||
    userEmail?.split("@")[0] ||
    "Lernender";

  const courseHasAccess =
    useCallback(
      (
        level: CourseLevelKey,
      ): boolean => {
        if (!isLoggedIn) {
          return false;
        }

        return Boolean(
          hasFullCourseAccess ||
            accessibleCourseLevels.includes(
              level,
            ) ||
            confirmedCourseLevel ===
              level,
        );
      },
      [
        accessibleCourseLevels,
        confirmedCourseLevel,
        hasFullCourseAccess,
        isLoggedIn,
      ],
    );

  const hasKnownCourseAccessState =
    Boolean(
      accessChecked ||
        hasFullCourseAccess ||
        accessibleCourseLevels.length >
          0,
    );

  const isInitialAccessLoading =
    Boolean(
      authLoading ||
        (isLoggedIn &&
          !hasKnownCourseAccessState &&
          accessLoading),
    );

  const isCheckingCourseAccess =
    Boolean(
      isInitialAccessLoading ||
        confirmingCourseAccess,
    );

  const selectedCourseIsUnlocked =
    Boolean(
      selectedCourseKey &&
        courseHasAccess(
          selectedCourseKey,
        ),
    );

  const isContentOpen =
    Boolean(
      selectedCourse &&
        selectedCourseKey &&
        isLoggedIn &&
        selectedCourseIsUnlocked,
    );

  const totalCourses =
    courseLevelOrder.length;

  const unlockedCourseCount =
    useMemo(() => {
      if (!isLoggedIn) {
        return 0;
      }

      if (hasFullCourseAccess) {
        return totalCourses;
      }

      return courseLevelOrder.filter(
        (level) =>
          courseHasAccess(level),
      ).length;
    }, [
      courseHasAccess,
      hasFullCourseAccess,
      isLoggedIn,
      totalCourses,
    ]);

  const headerTitle =
    hasFullCourseAccess
      ? "Alle Deutschkurse"
      : isLoggedIn
        ? "Meine Deutschkurse"
        : "Deutschkurse";

  const headerSubtitle =
    !isLoggedIn
      ? "Alle Kurse sind sichtbar. Zum Öffnen bitte einloggen oder Kurszugang kaufen."
      : hasFullCourseAccess
        ? "Vollzugang: Alle Kurse sind freigeschaltet."
        : "Freigeschaltete Kurse können sofort geöffnet werden. Gesperrte Kurse benötigen einen Kurszugang.";

  const visibleCourseAccessError =
    localCourseAccessError ||
    accessError ||
    "";

  const resetCoursePage =
    useCallback((): void => {
      setSelectedCourseKey(
        null,
      );

      setShowLoginModal(
        false,
      );

      setCourseAccessStatus(
        "idle",
      );

      setLocalCourseAccessError(
        "",
      );

      setConfirmingCourseAccess(
        false,
      );
    }, []);

  useEffect(() => {
    document.documentElement.classList.add(
      "course-screen-page",
    );

    document.body.classList.add(
      "course-screen-page",
    );

    return () => {
      document.documentElement.classList.remove(
        "course-screen-page",
      );

      document.body.classList.remove(
        "course-screen-page",
      );
    };
  }, []);

  useEffect(() => {
    const handleCourseReset =
      (): void => {
        resetCoursePage();
      };

    window.addEventListener(
      COURSE_RESET_EVENT,
      handleCourseReset,
    );

    return () => {
      window.removeEventListener(
        COURSE_RESET_EVENT,
        handleCourseReset,
      );
    };
  }, [resetCoursePage]);

  useEffect(() => {
    if (
      !confirmedCourseLevel
    ) {
      return;
    }

    if (
      hasFullCourseAccess ||
      accessibleCourseLevels.includes(
        confirmedCourseLevel,
      )
    ) {
      setConfirmedCourseLevel(
        null,
      );
    }
  }, [
    accessibleCourseLevels,
    confirmedCourseLevel,
    hasFullCourseAccess,
  ]);

  useEffect(() => {
    if (!selectedCourseKey) {
      setCourseAccessStatus(
        "idle",
      );

      return;
    }

    if (!isLoggedIn) {
      setCourseAccessStatus(
        "idle",
      );

      return;
    }

    if (
      confirmingCourseAccess
    ) {
      setCourseAccessStatus(
        "checking",
      );

      return;
    }

    if (
      courseHasAccess(
        selectedCourseKey,
      )
    ) {
      setCourseAccessStatus(
        "active",
      );

      setLocalCourseAccessError(
        "",
      );

      return;
    }

    if (
      visibleCourseAccessError &&
      accessChecked
    ) {
      setCourseAccessStatus(
        "error",
      );

      return;
    }

    setCourseAccessStatus(
      "inactive",
    );
  }, [
    accessChecked,
    confirmingCourseAccess,
    courseHasAccess,
    isLoggedIn,
    selectedCourseKey,
    visibleCourseAccessError,
  ]);

  const handleCourseSelect = (
    level: CourseLevelKey,
  ): void => {
    if (isInitialAccessLoading) {
      return;
    }

    setSelectedCourseKey(level);

    setLocalCourseAccessError(
      "",
    );

    if (!isLoggedIn) {
      setCourseAccessStatus(
        "idle",
      );

      setShowLoginModal(
        true,
      );

      return;
    }

    setCourseAccessStatus(
      courseHasAccess(level)
        ? "active"
        : "inactive",
    );
  };

  const handleCourseCardKeyDown = (
    event:
      React.KeyboardEvent<HTMLElement>,
    level: CourseLevelKey,
  ): void => {
    if (
      event.key !== "Enter" &&
      event.key !== " "
    ) {
      return;
    }

    event.preventDefault();

    handleCourseSelect(level);
  };

  const handleBackToCourses =
    (): void => {
      resetCoursePage();
    };

  const handleOpenLogin =
    (): void => {
      setShowLoginModal(true);
    };

  const handleLoginClose =
    (): void => {
      setShowLoginModal(false);
    };

  const handleLogout =
    async (): Promise<void> => {
      await logout();

      setConfirmedCourseLevel(
        null,
      );

      resetCoursePage();
    };

  const confirmPurchasedCourseAccess =
    useCallback(
      async (
        level: CourseLevelKey,
      ): Promise<boolean> => {
        setConfirmingCourseAccess(
          true,
        );

        setCourseAccessStatus(
          "checking",
        );

        setLocalCourseAccessError(
          "",
        );

        try {
          for (
            let attempt = 1;
            attempt <=
            ACCESS_CONFIRMATION_ATTEMPTS;
            attempt += 1
          ) {
            const nextAccess =
              await refreshAccess(
                true,
              );

            const confirmed =
              Boolean(
                nextAccess
                  .hasFullCourseAccess ||
                  nextAccess
                    .accessibleCourseLevels
                    .includes(level),
              );

            if (confirmed) {
              setConfirmedCourseLevel(
                level,
              );

              setCourseAccessStatus(
                "active",
              );

              setLocalCourseAccessError(
                "",
              );

              return true;
            }

            if (
              attempt <
              ACCESS_CONFIRMATION_ATTEMPTS
            ) {
              await delay(
                ACCESS_CONFIRMATION_DELAY_MS,
              );
            }
          }

          setCourseAccessStatus(
            "inactive",
          );

          setLocalCourseAccessError(
            "Die Zahlung wurde abgeschlossen, aber der Kurszugang wurde noch nicht bestätigt. Bitte klicken Sie auf „Zugang erneut prüfen“.",
          );

          return false;
        } catch (error) {
          setCourseAccessStatus(
            "error",
          );

          setLocalCourseAccessError(
            error instanceof Error
              ? error.message
              : "Der Kurszugang konnte nach der Zahlung nicht bestätigt werden.",
          );

          return false;
        } finally {
          setConfirmingCourseAccess(
            false,
          );
        }
      },
      [refreshAccess],
    );

  const handleSubscribed =
    (): void => {
      if (!selectedCourseKey) {
        return;
      }

      void confirmPurchasedCourseAccess(
        selectedCourseKey,
      );
    };

  const handleManualRefresh =
    useCallback(
      async (): Promise<void> => {
        if (
          !isLoggedIn ||
          confirmingCourseAccess
        ) {
          return;
        }

        setConfirmingCourseAccess(
          true,
        );

        setLocalCourseAccessError(
          "",
        );

        try {
          const nextAccess =
            await refreshAccess(
              true,
            );

          if (nextAccess.error) {
            setLocalCourseAccessError(
              nextAccess.error,
            );
          }
        } catch (error) {
          setLocalCourseAccessError(
            error instanceof Error
              ? error.message
              : "Der Kurszugang konnte nicht geprüft werden.",
          );
        } finally {
          setConfirmingCourseAccess(
            false,
          );
        }
      },
      [
        confirmingCourseAccess,
        isLoggedIn,
        refreshAccess,
      ],
    );

  const renderCourseDetailItem = (
    label: string,
    value: string,
    icon: React.ReactNode,
  ): React.ReactNode => {
    return (
      <div className="course-detail-chip">
        <span className="course-detail-chip-icon">
          {icon}
        </span>

        <span className="course-detail-chip-label">
          {label}
        </span>

        <strong className="course-detail-chip-value">
          {value}
        </strong>
      </div>
    );
  };

  const renderCardParticles =
    (): React.ReactNode => {
      return (
        <div
          className="course-card-particles"
          aria-hidden="true"
        >
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>
      );
    };

  const renderStatusNotice =
    (): React.ReactNode => {
      if (
        !isLoggedIn ||
        isCheckingCourseAccess ||
        !visibleCourseAccessError
      ) {
        return null;
      }

      return (
        <article className="course-learning-card course-status-card">
          {renderCardParticles()}

          <div className="course-card-badge course-card-badge-status">
            <section className="course-card-left">
              <div className="course-lock-block">
                <div className="course-level-icon">
                  <FaShieldAlt />
                </div>

                <span className="course-lock-status">
                  Zugang
                </span>
              </div>

              <div className="course-title-block">
                <h2>Fehler</h2>

                <p>
                  Bitte erneut versuchen
                </p>
              </div>
            </section>

            <section className="course-card-right">
              <div className="course-error-panel">
                <p>
                  {visibleCourseAccessError}
                </p>
              </div>

              <button
                type="button"
                className="course-refresh-button"
                onClick={(event) => {
                  event.stopPropagation();

                  void handleManualRefresh();
                }}
              >
                <span>
                  Erneut prüfen
                </span>

                <FaRedo />
              </button>
            </section>
          </div>
        </article>
      );
    };

  const renderLoginSection =
    (): React.ReactNode => {
      if (
        !selectedCourse ||
        !selectedCourseKey
      ) {
        return null;
      }

      return (
        <aside className="course-login-column course-payment-column">
          <section className="course-payment-wrapper">
            <article className="course-payment-card">
              <div
                className="course-payment-particles"
                aria-hidden="true"
              >
                <span />
                <span />
                <span />
                <span />
              </div>

              <button
                type="button"
                className="course-payment-back"
                onClick={
                  handleBackToCourses
                }
                aria-label="Zurück zu den Kursen"
              >
                ←
              </button>

              <div className="course-payment-top-row">
                <div className="course-payment-badge">
                  <FaShieldAlt />

                  <span>
                    Sicherer Zugang
                  </span>
                </div>
              </div>

              <h1 className="course-payment-title">
                Einloggen und Kurs öffnen
              </h1>

              <p className="course-payment-description">
                Bitte loggen Sie sich mit
                Ihrem Dear-Language-Konto
                ein. Danach wird Ihr
                vorhandener Kurszugang
                automatisch aus dem
                gemeinsamen Zugangsspeicher
                geladen.
              </p>

              <div className="course-payment-benefits">
                <div>
                  <FaCheckCircle />

                  <span>
                    Bestehenden Kurszugang
                    erkennen
                  </span>
                </div>

                <div>
                  <FaGraduationCap />

                  <span>
                    Freigeschalteten Kurs
                    sofort öffnen
                  </span>
                </div>

                <div>
                  <FaShieldAlt />

                  <span>
                    Sichere Anmeldung mit
                    Firebase
                  </span>
                </div>

                <div>
                  <FaLock />

                  <span>
                    Zahlung nur bei
                    gesperrtem Kurs
                  </span>
                </div>
              </div>

              <div className="course-payment-summary">
                <div>
                  <span>Kurs</span>

                  <strong>
                    {selectedCourse.title}
                  </strong>
                </div>

                <div>
                  <span>Niveau</span>

                  <strong>
                    {selectedCourseKey}
                  </strong>
                </div>

                <div>
                  <span>Dauer</span>

                  <strong>
                    {selectedCourse.duration}
                  </strong>
                </div>

                <div>
                  <span>Status</span>

                  <strong>
                    Anmeldung erforderlich
                  </strong>
                </div>
              </div>

              <div className="course-payment-actions">
                <button
                  type="button"
                  className="course-payment-main-button"
                  onClick={
                    handleOpenLogin
                  }
                >
                  <span>
                    Jetzt einloggen
                  </span>

                  <FaSignInAlt />
                </button>
              </div>

              <p className="course-payment-note">
                Nach dem Einloggen wird
                kein zusätzlicher
                Kurszugang geprüft, wenn
                der gemeinsame
                AuthContext-Cache noch
                aktuell ist.
              </p>
            </article>
          </section>
        </aside>
      );
    };

  const renderLoadingSection =
    (): React.ReactNode => {
      return (
        <aside className="course-login-column course-payment-column">
          <section className="course-payment-wrapper">
            <article className="course-payment-card">
              <div
                className="course-payment-particles"
                aria-hidden="true"
              >
                <span />
                <span />
                <span />
                <span />
              </div>

              <div className="course-payment-top-row">
                <div className="course-payment-badge">
                  <FaShieldAlt />

                  <span>Zugang</span>
                </div>
              </div>

              <h1 className="course-payment-title">
                Kurszugang wird
                aktualisiert
              </h1>

              <p className="course-payment-description">
                Bitte warten Sie kurz. Der
                Zugang wird nach der
                Zahlung oder der manuellen
                Aktualisierung bestätigt.
              </p>

              <div className="course-payment-benefits">
                <div>
                  <FaSpinner className="course-checking-icon" />

                  <span>
                    Kurszugang wird geprüft
                  </span>
                </div>

                <div>
                  <FaShieldAlt />

                  <span>
                    Sichere Prüfung über
                    Ihr Konto
                  </span>
                </div>
              </div>

              <p className="course-payment-note">
                Beim normalen Öffnen der
                Kursseite wird dieser
                Vorgang nicht erneut
                gestartet.
              </p>
            </article>
          </section>
        </aside>
      );
    };

  const renderPaymentSection =
    (): React.ReactNode => {
      if (
        !selectedCourse ||
        !selectedCourseKey
      ) {
        return null;
      }

      return (
        <aside className="course-login-column course-payment-column">
          <Payment
            courseLevel={
              selectedCourseKey
            }
            course={
              selectedCourse
            }
            userId={
              userId || ""
            }
            userEmail={
              userEmail
            }
            accessError={
              courseAccessStatus ===
                "error" ||
              localCourseAccessError
                ? visibleCourseAccessError
                : ""
            }
            onSubscribed={
              handleSubscribed
            }
            onLogout={
              handleLogout
            }
            onBackToCourses={
              handleBackToCourses
            }
          />
        </aside>
      );
    };

  return (
    <div
      className={`course-home-container ${
        isContentOpen
          ? "course-home-container-content-open"
          : ""
      }`}
    >
      <Login
        isOpen={showLoginModal}
        onClose={handleLoginClose}
      />

      <main
        className={`course-page ${
          isContentOpen
            ? "course-page-content-open"
            : ""
        }`}
      >
        {!selectedCourse && (
          <section className="course-level-section">
            <div className="course-screen-stack">
              <header className="course-header-card">
                <div className="course-header-content">
                  <span className="course-kicker">
                    Deutsch Akademie
                  </span>

                  <h1 className="course-page-title">
                    {headerTitle}
                  </h1>

                  <p className="course-subtitle">
                    {headerSubtitle}
                  </p>
                </div>

                <div className="course-header-badge">
                  <strong>
                    {unlockedCourseCount}/
                    {totalCourses}
                  </strong>

                  <span>
                    Freigeschaltete Kurse
                  </span>
                </div>
              </header>

              <div className="course-learning-grid">
                {courseLevelOrder.map(
                  (level) => {
                    const germanCourse =
                      getGermanCourseDisplay(
                        level,
                      );

                    const isUnlocked =
                      courseHasAccess(
                        level,
                      );

                    const cardStatusClassName =
                      isUnlocked
                        ? "course-card-unlocked"
                        : "course-card-locked";

                    const levelIconClassName =
                      isUnlocked
                        ? "course-level-icon-unlocked"
                        : "course-level-icon-locked";

                    const statusText =
                      isUnlocked
                        ? "Freigeschaltet"
                        : "Gesperrt";

                    return (
                      <button
                        key={level}
                        type="button"
                        className={`course-learning-card course-card-${level.toLowerCase()} ${cardStatusClassName}`}
                        onClick={() =>
                          handleCourseSelect(
                            level,
                          )
                        }
                        onKeyDown={(
                          event,
                        ) =>
                          handleCourseCardKeyDown(
                            event,
                            level,
                          )
                        }
                        aria-disabled={
                          isInitialAccessLoading
                        }
                        aria-label={`${germanCourse.title} öffnen`}
                      >
                        {renderCardParticles()}

                        <div className="course-card-badge">
                          <section className="course-card-left">
                            <div className="course-lock-block">
                              <div
                                className={`course-level-icon ${levelIconClassName}`}
                              >
                                {isUnlocked ? (
                                  <FaLockOpen />
                                ) : (
                                  <FaLock />
                                )}
                              </div>

                              <span className="course-lock-status">
                                {statusText}
                              </span>
                            </div>

                            <div className="course-title-block">
                              <h2>
                                {level}
                              </h2>

                              <p>
                                {
                                  germanCourse.label
                                }
                              </p>
                            </div>
                          </section>

                          <section className="course-card-right">
                            <div className="course-detail-chip course-detail-chip-main">
                              <span className="course-detail-chip-icon">
                                <FaGraduationCap />
                              </span>

                              <span className="course-detail-chip-label">
                                Kurs
                              </span>

                              <strong className="course-detail-chip-value">
                                {
                                  germanCourse.title
                                }
                              </strong>
                            </div>

                            {renderCourseDetailItem(
                              "Dauer",
                              germanCourse.duration,
                              <FaClock />,
                            )}

                            {renderCourseDetailItem(
                              "Unterricht",
                              germanCourse.sessions,
                              <FaGraduationCap />,
                            )}

                            {renderCourseDetailItem(
                              "Bücher",
                              germanCourse.books,
                              <FaBookOpen />,
                            )}
                          </section>
                        </div>
                      </button>
                    );
                  },
                )}

                {renderStatusNotice()}
              </div>
            </div>
          </section>
        )}

        {selectedCourse &&
          selectedCourseKey &&
          isLoggedIn &&
          isContentOpen && (
            <section
              className={`course-detail-wrapper course-content-open course-${selectedCourseKey.toLowerCase()}`}
            >
              <Content
                courseLevel={
                  selectedCourseKey
                }
                course={
                  selectedCourse
                }
                studentName={
                  loggedInName
                }
                isEnrolled={true}
              />
            </section>
          )}

        {selectedCourse &&
          selectedCourseKey &&
          !isContentOpen && (
            <section
              className={`course-detail-wrapper course-login-open course-${selectedCourseKey.toLowerCase()}`}
            >
              <div className="course-login-layout">
                {!isLoggedIn &&
                  !authLoading &&
                  renderLoginSection()}

                {isLoggedIn &&
                  confirmingCourseAccess &&
                  renderLoadingSection()}

                {isLoggedIn &&
                  !confirmingCourseAccess &&
                  !selectedCourseIsUnlocked &&
                  renderPaymentSection()}

                <CourseDetailSection
                  courseLevel={
                    selectedCourseKey
                  }
                  course={
                    selectedCourse
                  }
                  courseLabel={
                    selectedCourseLabel
                  }
                />
              </div>
            </section>
          )}
      </main>
    </div>
  );
};

export default CoursePage;